import mgrsLib from 'mgrs'
import type { DDCoordinate, DMSCoordinate } from '../../types/geo'
import { ddToDms } from './formats'

/**
 * Convert MGRS string to Decimal Degrees.
 * Returns null if invalid.
 */
export function mgrsToDD(mgrsStr: string): DDCoordinate | null {
  try {
    const normalized = normalizeMgrs(mgrsStr)
    const [lon, lat] = mgrsLib.toPoint(normalized) as [number, number] // [lon, lat] - GeoJSON order
    if (!isFinite(lat) || !isFinite(lon)) return null
    return { format: 'dd', lat, lon }
  } catch {
    return null
  }
}

/**
 * Convert Decimal Degrees to MGRS string.
 * precision: 1–5 (1=10km, 5=1m). Default 5.
 */
export function ddToMgrs(lat: number, lon: number, precision = 5): string | null {
  try {
    return mgrsLib.forward([lon, lat], precision) // [lon, lat] - GeoJSON order
  } catch {
    return null
  }
}

/**
 * Validate an MGRS string (normalized or spaced).
 */
export function isValidMgrs(mgrsStr: string): boolean {
  if (!mgrsStr || typeof mgrsStr !== 'string') return false
  try {
    const normalized = normalizeMgrs(mgrsStr)
    const result = mgrsLib.toPoint(normalized)
    return Array.isArray(result) && result.length === 2 && result.every(isFinite)
  } catch {
    return false
  }
}

/**
 * Return the meter precision of an MGRS string based on easting/northing digit count.
 * Returns null for invalid input.
 */
export function getMgrsPrecision(mgrsStr: string): number | null {
  try {
    const parsed = parseMgrsComponents(normalizeMgrs(mgrsStr))
    if (!parsed) return null
    const digits = parsed.easting.length  // same as northing
    const precisionMap: Record<number, number> = {
      1: 10000,
      2: 1000,
      3: 100,
      4: 10,
      5: 1,
    }
    return precisionMap[digits] ?? null
  } catch {
    return null
  }
}

/**
 * Convert MGRS to DMS coordinate pair.
 */
export function mgrsToDms(mgrsStr: string): DMSCoordinate | null {
  const dd = mgrsToDD(mgrsStr)
  if (!dd) return null
  return {
    format: 'dms',
    lat: ddToDms(dd.lat, 'lat'),
    lon: ddToDms(dd.lon, 'lon'),
  }
}

/**
 * Normalize MGRS by removing all spaces and upper-casing.
 * e.g. "18S UJ 23395 09512" → "18SUJ2339509512"
 */
export function normalizeMgrs(mgrsStr: string): string {
  return mgrsStr.replace(/\s+/g, '').toUpperCase()
}

/**
 * Format MGRS with standard spacing: "18S UJ 23395 09512"
 */
export function formatMgrs(mgrsStr: string): string {
  const norm = normalizeMgrs(mgrsStr)
  const parsed = parseMgrsComponents(norm)
  if (!parsed) return norm
  const { gzd, sq, easting, northing } = parsed
  return `${gzd} ${sq} ${easting} ${northing}`
}

export interface MgrsComponents {
  gzd: string       // e.g. "18S"
  sq: string        // 100km square, e.g. "UJ"
  easting: string   // 1–5 digits
  northing: string  // 1–5 digits (same count as easting)
}

/**
 * Parse MGRS into its components. Input must be normalized (no spaces, upper-case).
 * Returns null if unparseable.
 */
export function parseMgrsComponents(normalized: string): MgrsComponents | null {
  // MGRS pattern: optional zone number (1-2 digits) + zone letter + 2 letter sq + even number of digits
  const match = normalized.match(/^([0-9]{1,2}[C-HJ-NP-X])([A-HJ-NP-Z]{2})([0-9]{2,10})$/)
  if (!match) {
    // Could be polar (A-Z without zone)
    const polarMatch = normalized.match(/^([A-Z])([A-HJ-NP-Z]{2})([0-9]{2,10})$/)
    if (!polarMatch) return null
    const [, gzd, sq, digits] = polarMatch
    if (digits.length % 2 !== 0) return null
    const half = digits.length / 2
    return { gzd, sq, easting: digits.slice(0, half), northing: digits.slice(half) }
  }
  const [, gzd, sq, digits] = match
  if (digits.length % 2 !== 0) return null
  const half = digits.length / 2
  return { gzd, sq, easting: digits.slice(0, half), northing: digits.slice(half) }
}
